import { E } from '@angular/cdk/keycodes';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  skip,
  startWith,
  takeUntil,
} from 'rxjs/operators';

// SERVICES
import { debounceConfig } from '@app/core/_config';
import { IUnitOfMeasure } from 'v2/modules/unit-of-measure';

import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import {
  ChannelProductMappingApiService,
  UnitOfMeasureApiService,
} from '../../../shared/services';

// UTILS
import { trackById } from '../../../shared/util';
import { EChannelPushStockOMSProduct } from '../../interfaces';

@Component({
  selector: 'cnc-channel-product-mapping-search-uom-editor',
  templateUrl: './channel-product-mapping-search-uom-editor.component.html',
  styleUrls: ['./channel-product-mapping-search-uom-editor.component.scss'],
})
export class ChannelProductMappingSearchUomEditorComponent
  implements ICellEditorAngularComp, OnDestroy
{
  selectedUnit :IUnitOfMeasure | null = null;
  unitType :string | null = null;
  callbackOnChange :any = null;

  selectedItem :any = null; // TODO: Add type
  searchControl = new UntypedFormControl();
  searchTerm :string = '';
  previousSelectedItem :any | null = null; // TODO: Add type

  debounceTime = debounceConfig.time;

  trackById = trackById;

  private readonly _uoms = new BehaviorSubject<any[]>(<any[]>[]); // TODO: Add type
  uoms$ = this._uoms.asObservable();

  private readonly _loadingUoms = new BehaviorSubject<boolean>(<boolean>false);
  loadingUoms$ = this._loadingUoms.asObservable();

  private params :ICellEditorParams;
  private onDestroy$ = new Subject();

  constructor(
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private snackBar :MatSnackBar,
    private uomApiService :UnitOfMeasureApiService,
  ) {}

  agInit(params :ICellEditorParams) :void {
    let sellingUnit = params.data?.lumSku?.defaultSellingUnit || null;

    if (
      params.data?.productOverride?.sellingUnit ||
      params.data?.productOverride?.lumSku
    ) {
      sellingUnit =
        params.data?.productOverride?.sellingUnit ||
        params.data?.productOverride?.lumSku?.defaultSellingUnit;
    }

    if (!sellingUnit) {
      this.searchControl.disable();

      return;
    }

    this.params = params;
    this.selectedItem = sellingUnit || null;
    this.previousSelectedItem = this.selectedItem;

    this.searchUom('');
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  displaySelectedItem() :string {
    return this.formattedSelectedItem;
  }

  selectValue(item :any) :void {
    this.selectedItem = item;
    if (this.params.colDef.field === EChannelPushStockOMSProduct.OMS_DEFAULT_UNIT) {
      return;
    }
    this.updateChannelPushStockOmsProductSkuMapping();
  }

  onSearch(keyword :string) :void {
    this.searchUom(keyword);
  }

  private searchUom(keyword :string) :void {
    this._loadingUoms.next(true);
    this.uomApiService
      .searchByUnitClass(this.selectedItem?.unitClassId, { name: keyword })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingUoms.next(false);
        }),
      )
      .subscribe({
        next: (rfqs :any[]) => {
          this._uoms.next(rfqs);
        },
        error: (error :any) => {
          this.snackBar.open('Error when seaching sku.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private updateChannelPushStockOmsProductSkuMapping() :void {
    const payload = {
      channelId: this.params.data?.channelId,
      sellingUnitId: (this.selectedItem?.id || 0).toString(), // 0 = unmap
    };

    this.channelProductMappingApiService
      .saveChannelProductOverride(this.params.data.id, payload)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.params.stopEditing();
          this.params.api.stopEditing();
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open('Successfully mapped selling unit.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error :any) => {
          // rollback
          this.selectedItem = this.previousSelectedItem;

          this.snackBar.open('Failed to map selling unit.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  getValue() :any {
    return this.selectedItem;
  }

  get formattedSelectedItem() :string {
    if (!this.selectedItem) {
      return '';
    }

    return this.selectedItem.name;
  }
}
